import axios from "axios";
import { randomUuid } from "../../shared/randomUuid";

/**
 * PUBLIC patient self-service portal API + session store.
 *
 * <p>This module deliberately uses a BARE axios instance — never the staff
 * {@code shared/api/client.ts} apiClient — because that client injects the
 * staff bearer token + {@code X-Addy-Tenant-Id} from the staff localStorage
 * keys. The portal is a separate area: a patient logs in with phone + OTP and
 * gets their OWN short-lived {@code accessToken}. We keep that token under
 * DISTINCT localStorage keys so it never clashes with (or leaks into) a staff
 * session on the same browser.
 *
 * <p>Base path mirrors {@code publicQueueApi} / {@code geoApi}: same-origin
 * nginx proxies {@code /api}, so {@code VITE_API_BASE_URL ?? "/api"} then
 * {@code /v1/...}.
 */

const BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

const http = axios.create({ baseURL: BASE, timeout: 30_000 });

// ---------------------------------------------------------------------------
// Session store — DISTINCT keys from staff ("addy.accessToken"/"addy.tenantId")
// ---------------------------------------------------------------------------

const PORTAL_TOKEN_KEY = "addy.portal.accessToken";
const PORTAL_TENANT_KEY = "addy.portal.tenantId";
const PORTAL_NAME_KEY = "addy.portal.patientName";
const PORTAL_CLINIC_KEY = "addy.portal.clinicName";
const PORTAL_EXPIRES_KEY = "addy.portal.expiresAt";

export interface PortalSession {
  accessToken: string;
  tenantId: string;
  patientName: string;
  clinicName: string;
  expiresAt: string | null;
}

export function getPortalSession(): PortalSession | null {
  const accessToken = localStorage.getItem(PORTAL_TOKEN_KEY);
  const tenantId = localStorage.getItem(PORTAL_TENANT_KEY);
  if (!accessToken || !tenantId) return null;

  // Drop an expired session so the dashboard guard redirects cleanly.
  const expiresAt = localStorage.getItem(PORTAL_EXPIRES_KEY);
  if (expiresAt && Date.parse(expiresAt) <= Date.now()) {
    clearPortalSession();
    return null;
  }

  return {
    accessToken,
    tenantId,
    patientName: localStorage.getItem(PORTAL_NAME_KEY) ?? "",
    clinicName: localStorage.getItem(PORTAL_CLINIC_KEY) ?? "",
    expiresAt,
  };
}

export function setPortalSession(s: PortalSession): void {
  localStorage.setItem(PORTAL_TOKEN_KEY, s.accessToken);
  localStorage.setItem(PORTAL_TENANT_KEY, s.tenantId);
  localStorage.setItem(PORTAL_NAME_KEY, s.patientName);
  localStorage.setItem(PORTAL_CLINIC_KEY, s.clinicName);
  if (s.expiresAt) localStorage.setItem(PORTAL_EXPIRES_KEY, s.expiresAt);
  else localStorage.removeItem(PORTAL_EXPIRES_KEY);
}

export function clearPortalSession(): void {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_TENANT_KEY);
  localStorage.removeItem(PORTAL_NAME_KEY);
  localStorage.removeItem(PORTAL_CLINIC_KEY);
  localStorage.removeItem(PORTAL_EXPIRES_KEY);
}

/**
 * Auth headers for the patient's own /me calls: BOTH the portal bearer AND the
 * tenant id (the public/patient endpoints require both). Read from the session
 * store — never from the staff keys.
 */
function meHeaders(): Record<string, string> {
  const s = getPortalSession();
  if (!s)
    throw new PortalError(
      "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
      401,
    );
  return {
    Authorization: `Bearer ${s.accessToken}`,
    "X-Addy-Tenant-Id": s.tenantId,
  };
}

// ---------------------------------------------------------------------------
// Error helper — carries a Vietnamese message + HTTP status for the UI
// ---------------------------------------------------------------------------

export class PortalError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "PortalError";
    this.status = status;
  }
}

/** Extract the backend RFC-7807 `detail` (Vietnamese) or fall back. */
function toPortalError(err: unknown, fallback: string): PortalError {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const data = err.response?.data as
      | { detail?: string; message?: string }
      | undefined;
    const detail = data?.detail ?? data?.message;
    return new PortalError(detail && detail.trim() ? detail : fallback, status);
  }
  return new PortalError(fallback, 0);
}

// ---------------------------------------------------------------------------
// Public auth flow: clinic -> request-otp -> verify-otp
// ---------------------------------------------------------------------------

export interface ClinicInfo {
  tenantId: string;
  clinicName: string;
}

export async function resolveClinic(slug: string): Promise<ClinicInfo> {
  try {
    const res = await http.get<ClinicInfo>("/v1/public/patient-portal/clinic", {
      params: { slug },
    });
    return res.data;
  } catch (err) {
    throw toPortalError(err, "Không tìm thấy phòng khám với mã này.");
  }
}

export interface RequestOtpResult {
  sent: boolean;
  /** Populated only in the dev environment for testing. */
  devCode: string | null;
}

export async function requestOtp(
  tenantId: string,
  phone: string,
): Promise<RequestOtpResult> {
  try {
    const res = await http.post<RequestOtpResult>(
      "/v1/public/patient-portal/request-otp",
      { tenantId, phone },
    );
    return res.data;
  } catch (err) {
    // No "chưa có hồ sơ" here: request-otp answers every valid number the same
    // way (#19145). A 429 carries its own Vietnamese detail (wait time).
    throw toPortalError(err, "Chưa gửi được yêu cầu. Vui lòng thử lại.");
  }
}

export interface ProfileOption {
  patientId: string;
  /** "Nguyễn T. T." — never the full name (#19147). */
  maskedName: string;
  birthYear: number | null;
  gender: string | null;
}

export interface VerifyOtpResult {
  patientId: string;
  patientName: string;
  accessToken: string;
  expiresAt: string;
  /**
   * #19147 — one phone, several records: no session yet. The patient picks
   * one of `profiles` and the page calls select-profile with this token.
   */
  selectionToken?: string | null;
  profiles?: ProfileOption[] | null;
  /**
   * #19148 — the patient protected their results with a PIN (set in the Mini
   * App). The token above can ONLY call verify-pin: every /patients/me call
   * answers 403 until the PIN is entered. Never store it as the session.
   */
  pinRequired: boolean;
}

export interface VerifyPinResult {
  ok: boolean;
  remainingAttempts: number;
  lockedUntil: string | null;
  /** Set only when ok: the full session (the pending token is revoked). */
  session: VerifyOtpResult | null;
}

/** Pick one of the records the OTP covered (#19147). Single-use token. */
export async function selectProfile(
  selectionToken: string,
  patientId: string,
): Promise<VerifyOtpResult> {
  try {
    const res = await http.post<VerifyOtpResult>(
      "/v1/public/patient-portal/select-profile",
      { selectionToken, patientId },
    );
    return res.data;
  } catch (err) {
    throw toPortalError(
      err,
      "Phiên chọn hồ sơ đã hết hạn. Vui lòng đăng nhập lại.",
    );
  }
}

/** The PIN step after the OTP (#19148). Bearer = the pending token. */
export async function verifyPin(
  pendingToken: string,
  pin: string,
): Promise<VerifyPinResult> {
  try {
    const res = await http.post<VerifyPinResult>(
      "/v1/public/patient-portal/verify-pin",
      { pin },
      { headers: { Authorization: `Bearer ${pendingToken}` } },
    );
    return res.data;
  } catch (err) {
    throw toPortalError(err, "Chưa kiểm tra được mã PIN. Vui lòng thử lại.");
  }
}

/**
 * Log out ON THE SERVER (#19148): the token is revoked now, not left valid
 * until it expires. Best-effort — the local session is cleared either way.
 */
export async function logoutPortal(): Promise<void> {
  const s = getPortalSession();
  clearPortalSession();
  if (!s) return;
  try {
    await http.post("/v1/public/patient-portal/logout", null, {
      headers: { Authorization: `Bearer ${s.accessToken}` },
    });
  } catch {
    // Already expired/revoked, or offline: nothing more to do locally.
  }
}

export async function verifyOtp(
  tenantId: string,
  phone: string,
  code: string,
): Promise<VerifyOtpResult> {
  try {
    const res = await http.post<VerifyOtpResult>(
      "/v1/public/patient-portal/verify-otp",
      { tenantId, phone, code },
    );
    return res.data;
  } catch (err) {
    throw toPortalError(err, "Mã OTP không đúng hoặc đã hết hạn.");
  }
}

// ---------------------------------------------------------------------------
// Authenticated "me" getters — send BOTH Authorization + X-Addy-Tenant-Id
// ---------------------------------------------------------------------------

export interface MeProfile {
  fullName: string | null;
  phone: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  bhytCardNo: string | null;
  email: string | null;
  addressLine: string | null;
  provinceCode: string | null;
  wardCode: string | null;
  [key: string]: unknown;
}

/**
 * Bốn danh sách `/patients/me/*` gộp cả người thân đã liên kết (đặt lịch hộ, hoặc người được
 * khám tách ra khi chuyển dữ liệu): `patientName` là tên người khám, `self` = chính chủ tài
 * khoản. Cả hai tuỳ chọn vì bản API cũ không có — thiếu thì coi như của chính chủ.
 */
export interface MeAppointment {
  id: string;
  doctorName: string | null;
  serviceName: string | null;
  startAt: string | null;
  status: string | null;
  notes: string | null;
  patientName?: string | null;
  self?: boolean;
}

export interface MePrescription {
  id: string;
  doctorName: string | null;
  status: string | null;
  issuedAt: string | null;
  itemCount: number | null;
  patientName?: string | null;
  self?: boolean;
  [key: string]: unknown;
}

export interface MeHistoryEntry {
  id: string;
  doctorName?: string | null;
  serviceName?: string | null;
  examDate?: string | null;
  startAt?: string | null;
  status?: string | null;
  diagnosis?: string | null;
  chiefComplaint?: string | null;
  patientName?: string | null;
  self?: boolean;
  [key: string]: unknown;
}

/** Nhãn "Người khám: …" chỉ khi dòng là của một người thân; dòng của chính chủ không có nhãn. */
export function relativeLabel(row: { patientName?: string | null; self?: boolean }): string | null {
  if (row.self !== false) return null;
  return row.patientName?.trim() || null;
}

async function meGet<T>(path: string, fallback: string): Promise<T> {
  try {
    const res = await http.get<T>(path, { headers: meHeaders() });
    return res.data;
  } catch (err) {
    const pe = toPortalError(err, fallback);
    // A 401/403 means the portal token is stale — clear so the guard bounces.
    if (pe.status === 401 || pe.status === 403) clearPortalSession();
    throw pe;
  }
}

export function meProfile(): Promise<MeProfile> {
  return meGet<MeProfile>("/v1/patients/me/profile", "Không tải được hồ sơ.");
}

export function meAppointments(): Promise<MeAppointment[]> {
  return meGet<MeAppointment[]>(
    "/v1/patients/me/appointments",
    "Không tải được lịch hẹn.",
  );
}

export function mePrescriptions(): Promise<MePrescription[]> {
  return meGet<MePrescription[]>(
    "/v1/patients/me/prescriptions",
    "Không tải được đơn thuốc.",
  );
}

export function meHistory(): Promise<MeHistoryEntry[]> {
  return meGet<MeHistoryEntry[]>(
    "/v1/patients/me/history",
    "Không tải được lịch sử khám.",
  );
}

// ---------------------------------------------------------------------------
// Phiếu khám ngoài (MeetDoctor) quét từ QR → HIS xử lý như salepro: bệnh nhân, dịch vụ, lượt khám
// hoàn thành, lịch tái khám (POST/GET /v1/patients/me/external-slips — ExternalSlipView)
// ---------------------------------------------------------------------------

export interface ExternalSlipServiceLine {
  name: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface ExternalSlip {
  id: string;
  patientId: string;
  source: string;
  patientName: string | null;
  examDate: string | null;
  nextVisitDate: string | null;
  diagnosis: string | null;
  services: ExternalSlipServiceLine[];
  discountAmount: number;
  totalAmount: number;
  prescriptionHtml: string | null;
  labResultHtml: string | null;
  importedAt: string;
  /** Lịch tái khám HIS tự đặt từ nextVisitDate; null = không có ngày / đã qua / đặt hỏng. */
  followUpAppointmentId: string | null;
  /** Lượt khám (đã hoàn thành) ghi nhận từ phiếu — "Xem kết quả khám" trên lịch hẹn mở phiếu này. */
  visitAppointmentId: string | null;
}

export interface ImportSlipResult {
  slip: ExternalSlip;
  /** true = phiếu đã có trong hồ sơ (200), false = vừa lưu (201). */
  alreadyImported: boolean;
}

/**
 * #19819 — helper cục bộ cũ ở đây đã phòng vệ đúng chỗ (`if (c?.randomUUID)`) nhưng rơi về
 * `Date.now()-Math.random()`, và nó là ĐỊNH NGHĨA THỨ HAI cho cùng một việc: tám chỗ gọi khác
 * trong his-web gọi thẳng `crypto.randomUUID()` và chết trên HTTP. Một hàm dùng chung thì lần sau
 * không ai phải tự nhớ lại chuyện secure context.
 */
function idempotencyKey(): string {
  return randomUuid();
}

/** Nội dung mã QR, nguyên văn như máy quét trả về (HIS tự chuẩn hoá địa chỉ). */
export async function importExternalSlip(url: string): Promise<ImportSlipResult> {
  try {
    const res = await http.post<ImportSlipResult>(
      "/v1/patients/me/external-slips",
      { url },
      { headers: { ...meHeaders(), "Idempotency-Key": idempotencyKey() } },
    );
    return res.data;
  } catch (err) {
    const pe = toPortalError(err, "Không đưa được phiếu khám vào hồ sơ.");
    if (pe.status === 401) clearPortalSession();
    throw pe;
  }
}

export function listExternalSlips(): Promise<ExternalSlip[]> {
  return meGet<ExternalSlip[]>(
    "/v1/patients/me/external-slips",
    "Không tải được danh sách phiếu khám.",
  );
}

export function getExternalSlip(id: string): Promise<ExternalSlip> {
  return meGet<ExternalSlip>(
    `/v1/patients/me/external-slips/${encodeURIComponent(id)}`,
    "Không tải được phiếu khám.",
  );
}

/** Chỉ cho phép quay về một trang trong cổng bệnh nhân, không bao giờ ra ngoài. */
export function safePortalPath(next: string | null | undefined): string | null {
  if (!next) return null;
  if (!next.startsWith("/portal/") || next.startsWith("//")) return null;
  return next;
}

